import React, { useRef, useEffect, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface PdfCanvasProps {
	pdfUrl: string;
	onTextExtracted?: (text: string) => void;
}

interface PageRender {
	pageNum: number;
	canvas: HTMLCanvasElement;
}

const BASE_SCALE = 1.5;
const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

const PdfCanvas: React.FC<PdfCanvasProps> = ({ pdfUrl, onTextExtracted }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [pages, setPages] = useState<PageRender[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [zoom, setZoom] = useState(1);

	useEffect(() => {
		if (!pdfUrl) return;
		let cancelled = false;

		async function loadPdf() {
			try {
				setLoading(true);
				setError(null);
				setZoom(1);

				const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
				const renderedPages: PageRender[] = [];
				let concatenatedText = "";

				for (let i = 1; i <= pdf.numPages; i++) {
					const page = await pdf.getPage(i);
					const viewport = page.getViewport({ scale: BASE_SCALE });

					const canvas = document.createElement("canvas");
					canvas.width = viewport.width;
					canvas.height = viewport.height;
					const ctx = canvas.getContext("2d")!;
					await page.render({ canvasContext: ctx, viewport }).promise;

					const textContent = await page.getTextContent();
					concatenatedText += textContent.items
						.filter((item: any) => item.str !== undefined)
						.map((item: any) => item.str)
						.join("") + "\n";

					renderedPages.push({ pageNum: i, canvas });
				}

				if (!cancelled) {
					setPages(renderedPages);
					setLoading(false);
					if (onTextExtracted) onTextExtracted(concatenatedText);
				}
			} catch (err: any) {
				if (!cancelled) {
					setError(err.message || "Failed to load PDF");
					setLoading(false);
				}
			}
		}

		loadPdf();
		return () => { cancelled = true; };
	}, [pdfUrl]); // eslint-disable-line react-hooks/exhaustive-deps

	const renderPages = useCallback(() => {
		const container = containerRef.current;
		if (!container || pages.length === 0) return;
		container.innerHTML = "";

		for (const page of pages) {
			const wrapper = document.createElement("div");
			wrapper.style.marginBottom = "8px";
			wrapper.style.background = "#fff";
			wrapper.style.boxShadow = "0 2px 8px rgba(0,0,0,.1)";
			wrapper.style.borderRadius = "4px";
			wrapper.style.overflow = "hidden";
			wrapper.style.width = `${zoom * 100}%`;
			wrapper.style.transformOrigin = "top left";

			page.canvas.style.display = "block";
			page.canvas.style.width = "100%";
			page.canvas.style.height = "auto";
			wrapper.appendChild(page.canvas.cloneNode(true) as HTMLCanvasElement);

			// Copy pixel data to cloned canvas
			const clone = wrapper.querySelector("canvas")!;
			clone.width = page.canvas.width;
			clone.height = page.canvas.height;
			clone.getContext("2d")!.drawImage(page.canvas, 0, 0);

			container.appendChild(wrapper);
		}
	}, [pages, zoom]);

	useEffect(() => {
		renderPages();
	}, [renderPages]);

	if (loading) {
		return (
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, color: "#9aa0b4", fontSize: 13 }}>
				Loading PDF...
			</div>
		);
	}

	if (error) {
		return (
			<div style={{ padding: 20, textAlign: "center", color: "#dc3b52", fontSize: 13 }}>
				Failed to render PDF: {error}
			</div>
		);
	}

	const pct = Math.round(zoom * 100);

	return (
		<div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
			<div style={{ flex: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "6px 10px", borderBottom: "1px solid #e4e6ef", background: "#f5f5f8" }}>
				<button
					onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))}
					disabled={zoom <= MIN_ZOOM}
					style={{ border: "1px solid #dcdfea", background: "#fff", borderRadius: 5, width: 26, height: 26, cursor: zoom <= MIN_ZOOM ? "default" : "pointer", fontSize: 15, fontWeight: 700, color: zoom <= MIN_ZOOM ? "#ccc" : "#5f6478", display: "flex", alignItems: "center", justifyContent: "center" }}
					title="Zoom out"
				>-</button>
				<span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11, color: "#5f6478", minWidth: 40, textAlign: "center" }}>{pct}%</span>
				<button
					onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))}
					disabled={zoom >= MAX_ZOOM}
					style={{ border: "1px solid #dcdfea", background: "#fff", borderRadius: 5, width: 26, height: 26, cursor: zoom >= MAX_ZOOM ? "default" : "pointer", fontSize: 15, fontWeight: 700, color: zoom >= MAX_ZOOM ? "#ccc" : "#5f6478", display: "flex", alignItems: "center", justifyContent: "center" }}
					title="Zoom in"
				>+</button>
				<button
					onClick={() => setZoom(1)}
					disabled={zoom === 1}
					style={{ border: "1px solid #dcdfea", background: "#fff", borderRadius: 5, padding: "0 8px", height: 26, cursor: zoom === 1 ? "default" : "pointer", fontSize: 10, fontWeight: 700, color: zoom === 1 ? "#ccc" : "#5f6478", fontFamily: "'Sora',sans-serif" }}
				>Reset</button>
			</div>
			<div
				ref={containerRef}
				style={{
					flex: 1,
					overflowY: "auto",
					overflowX: zoom > 1 ? "auto" : "hidden",
					background: "#e8e9ed",
					padding: 12,
					minHeight: 0,
				}}
			/>
		</div>
	);
};

export default PdfCanvas;
